import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Palmtree } from "lucide-react";
import { NAV_LINKS } from "../../data";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = NAV_LINKS.map((l) => l.href.slice(1));
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin: "-45% 0px -50% 0px" }
    );
    ids.forEach((id) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed top-3 sm:top-4 inset-x-0 mx-auto w-[94%] max-w-6xl z-50"
      data-testid="main-navbar"
    >
      <div
        className={`rounded-full px-5 sm:px-7 py-3 flex items-center justify-between border transition-all duration-500 ${
          scrolled
            ? "bg-sand-light/85 border-gold/30 shadow-[0_10px_40px_rgba(10,34,40,0.12)] backdrop-blur-xl"
            : "bg-white/10 border-white/20 backdrop-blur-md"
        }`}
      >
        <button
          onClick={() => go("#hero")}
          className={`flex items-center gap-2 font-serif text-lg sm:text-xl font-semibold tracking-tight transition-colors ${
            scrolled ? "text-ocean" : "text-white"
          }`}
          data-testid="nav-logo"
        >
          <Palmtree className="w-5 h-5 text-gold" strokeWidth={1.6} />
          L'Échappée CSE
        </button>

        <div className="hidden lg:flex items-center gap-6">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className={`text-sm font-medium whitespace-nowrap transition-colors hover:text-gold ${
                active === l.href.slice(1) ? "text-gold" : scrolled ? "text-ocean/80" : "text-white/90"
              }`}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
            >
              {l.name}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => go("#formulaire-devis")}
            className="hidden sm:inline-flex items-center rounded-full bg-coral hover:bg-coral-hover text-white text-sm font-semibold px-5 py-2.5 transition-all hover:shadow-[0_8px_24px_rgba(230,92,0,0.4)] hover:-translate-y-0.5"
            data-testid="nav-cta-button"
          >
            Demander une proposition
          </button>
          <button
            className={`lg:hidden p-2 rounded-full ${scrolled ? "text-ocean" : "text-white"}`}
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden mt-2 rounded-3xl bg-sand-light/95 backdrop-blur-xl border border-gold/30 shadow-xl p-4 flex flex-col gap-1"
            data-testid="nav-mobile-menu"
          >
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className={`text-left px-4 py-3 rounded-2xl font-medium transition-colors ${
                  active === l.href.slice(1) ? "bg-palm-light text-gold" : "text-ocean hover:bg-palm-light"
                }`}
              >
                {l.name}
              </button>
            ))}
            <button
              onClick={() => go("#formulaire-devis")}
              className="mt-1 rounded-full bg-coral text-white font-semibold px-5 py-3"
              data-testid="nav-mobile-cta"
            >
              Demander une proposition
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
